import React, { useEffect, useRef, useState } from 'react';

declare global {
    interface Window {
        MercadoPago: any;
    }
}

interface CardPaymentFormProps {
    amount: number;
    onSubmit: (paymentData: any) => void;
    isProcessing: boolean;
    userEmail: string;
}

export const CardPaymentForm: React.FC<CardPaymentFormProps> = ({ amount, onSubmit, isProcessing, userEmail }) => {
    const cardPaymentBrickController = useRef<any>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string>('');
    const isInitialized = useRef(false);

    useEffect(() => {
        // Prevenir inicialização dupla
        if (isInitialized.current) return;
        isInitialized.current = true;

        const initializeMercadoPago = async () => {
            try {
                setIsLoading(true);
                setError('');

                // Verificar se já existe um script do Mercado Pago
                const existingScript = document.querySelector('script[src="https://sdk.mercadopago.com/js/v2"]');

                if (!existingScript) {
                    // Carregar SDK do Mercado Pago se não existir
                    const script = document.createElement('script');
                    script.src = 'https://sdk.mercadopago.com/js/v2';
                    script.async = true;

                    await new Promise((resolve, reject) => {
                        script.onload = resolve;
                        script.onerror = reject;
                        document.body.appendChild(script);
                    });
                }

                // Aguardar um momento para o SDK estar completamente disponível
                await new Promise(resolve => setTimeout(resolve, 100));

                if (!window.MercadoPago) {
                    throw new Error('SDK do Mercado Pago não carregado');
                }

                const mp = new window.MercadoPago(process.env.REACT_APP_MERCADOPAGO_PUBLIC_KEY);
                const bricksBuilder = mp.bricks();

                // Limpar container antes de renderizar
                const container = document.getElementById('cardPaymentBrick_container');
                if (container) {
                    // Remover todos os filhos de forma segura sem usar innerHTML
                    while (container.firstChild) {
                        container.removeChild(container.firstChild);
                    }
                }

                // Renderizar o Card Payment Brick
                cardPaymentBrickController.current = await bricksBuilder.create(
                    'cardPayment',
                    'cardPaymentBrick_container',
                    {
                        initialization: {
                            amount: amount,
                            payer: {
                                email: userEmail
                            }
                        },
                        customization: {
                            visual: {
                                hideFormTitle: true,
                                style: {
                                    theme: 'default',
                                    customVariables: {
                                        baseColor: '#3b82f6',
                                        textPrimaryColor: '#1f2937',
                                        textSecondaryColor: '#6b7280',
                                        inputBackgroundColor: '#ffffff',
                                        formBackgroundColor: '#f9fafb',
                                    }
                                }
                            },
                            paymentMethods: {
                                maxInstallments: 12,
                                minInstallments: 1,
                            }
                        },
                        callbacks: {
                            onReady: () => {
                                setIsLoading(false);
                            },
                            onSubmit: async (cardFormData: any) => {

                                // Preparar dados para enviar ao backend
                                const paymentData = {
                                    token: cardFormData.token,
                                    payment_method_id: cardFormData.payment_method_id,
                                    issuer_id: cardFormData.issuer_id,
                                    installments: cardFormData.installments,
                                    payer: {
                                        email: userEmail, // Usar email do usuário logado
                                        firstName: cardFormData.payer?.first_name || '',
                                        lastName: cardFormData.payer?.last_name || '',
                                        identification: cardFormData.payer?.identification
                                    }
                                };

                                // Chamar callback de submit
                                onSubmit(paymentData);

                                return new Promise<void>((resolve) => {
                                    // Retornar promise para o Brick saber quando terminou
                                    setTimeout(() => {
                                        resolve();
                                    }, 1000);
                                });
                            },
                            onError: (error: any) => {
                                console.error('Erro no Card Payment Brick:', error);
                                setError('Erro ao carregar formulário de pagamento');
                            }
                        },
                        locale: 'pt-BR'
                    }
                );

                setIsLoading(false);

            } catch (error) {
                console.error('Erro ao carregar Mercado Pago Brick:', error);
                setError('Erro ao carregar formulário de pagamento. Tente recarregar a página.');
                setIsLoading(false);
            }
        };

        initializeMercadoPago();

        // Cleanup
        return () => {
            if (cardPaymentBrickController.current) {
                try {
                    cardPaymentBrickController.current.unmount();
                } catch (e) {
                    console.log('Erro ao desmontar brick:', e);
                }
                    // Brick unmount error silently ignored
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [amount, onSubmit]);

    return (
        <div className="space-y-4 relative">
            {/* Loading state */}
            {isLoading && (
                <div className="absolute inset-0 bg-white bg-opacity-90 flex items-center justify-center rounded-lg z-10 min-h-[400px]">
                    <div className="text-center">
                        <svg className="animate-spin h-12 w-12 text-brand-blue mx-auto mb-4" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <p className="text-gray-600 font-semibold">Carregando formulário...</p>
                    </div>
                </div>
            )}

            {/* Error state */}
            {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                    <div className="flex items-start gap-3">
                        <svg className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <div>
                            <p className="font-semibold text-red-900">Erro ao carregar formulário</p>
                            <p className="text-sm text-red-700 mt-1">{error}</p>
                        </div>
                    </div>
                </div>
            )}

            {/* Container onde o Mercado Pago irá renderizar o formulário */}
            <div id="cardPaymentBrick_container" className="min-h-[400px]"></div>

            {/* Processing overlay */}
            {isProcessing && (
                <div className="absolute inset-0 bg-white bg-opacity-90 flex items-center justify-center rounded-lg z-20">
                    <div className="text-center">
                        <svg className="animate-spin h-12 w-12 text-brand-blue mx-auto mb-4" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <p className="text-gray-600 font-semibold">Processando pagamento...</p>
                    </div>
                </div>
            )}

            <div className="flex items-center justify-center gap-2 text-xs text-gray-500 mt-4">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                <span>Pagamento 100% seguro via Mercado Pago</span>
            </div>
        </div>
    );
};





